import {
  useMutation,
  UseMutationResult,
} from '@tanstack/react-query';
import {
  MediaSettingsDTO,
  PatientMultiMediaResponseDTO,
  PatientMultiMediaUploadDTO,
} from 'dtos';
import {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import {
  getApiSettingsGetMediaSettings,
  postApiMultimediaPostPatientMultiMedia,
} from 'endpoints';
import {
  sanitizeFileName,
} from '@populateui/utils';
import usePrefetchedData from 'pages/Dashboard/hooks/usePrefetchedData';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';

type UploadMediaFileParams = {
  data: FileLocalUrl[];
  patientId: number;
  fileType: string;
};

type UploadPatientMedia = UseMutationResult<
 PatientMultiMediaResponseDTO,
 Error,
 PatientMultiMediaUploadDTO
 > & {
    uploadAllMedia: (params: UploadMediaFileParams) => Promise<PatientMultiMediaResponseDTO[]>;
  };

export function useGetMediaSettings(): MediaSettingsDTO {
  const { isRestricted } = useEMRAccess();
  const { data } = usePrefetchedData<MediaSettingsDTO>({
    key: ['media-settings'],
    fetchFn: getApiSettingsGetMediaSettings,
    enabled: !isRestricted,
  });

  return data ?? {};
}

export default function useUploadPatientMedia(): UploadPatientMedia {
  const {
    mutateAsync: uploadMedia,
    ...mutation
  } = useMutation<PatientMultiMediaResponseDTO, Error, PatientMultiMediaUploadDTO>({
    mutationFn: (media) => postApiMultimediaPostPatientMultiMedia(media),
  });

  const uploadAllMedia = (
    { data,
      patientId,
      fileType }: UploadMediaFileParams,
  ) => Promise.all(
    data?.map(({ dataUrl, file }) =>
      uploadMedia({
        patientId,
        fileName: sanitizeFileName(file?.name ?? ''),
        dataUrl: dataUrl ?? '',
        multimediaType: fileType,
      })),
  );
  return { ...mutation, uploadAllMedia, mutateAsync: uploadMedia };
}
